/** Pure helpers for the Expedition Loadout play screen (the backpack drawing, the trip review). */
import {
  BACKPACK_SLOTS,
  HOME,
  MAX_COPIES,
  blocked,
  choicesOf,
  itemById,
  slotsUsed,
  start,
  step,
  type Action,
  type Adventure,
  type ExpChoice,
  type ExpState,
  type ItemId,
  type Loadout,
} from './expedition.ts';

export type BackpackSquare = { id: ItemId; first: boolean } | null;

/** The backpack's 12 squares: each packed copy fills its slots, in shelf order. */
export function backpackSquares(a: Adventure, loadout: Loadout): BackpackSquare[] {
  const out: BackpackSquare[] = [];
  for (const id of a.shelf)
    for (let c = 0; c < (loadout[id] ?? 0); c++)
      for (let s = 0; s < itemById(id)!.slots; s++) out.push({ id, first: s === 0 });
  while (out.length < BACKPACK_SLOTS) out.push(null);
  return out;
}

export type StopStatus = 'best' | 'ok' | 'home' | 'not-reached';

export interface TripReview {
  stops: { status: StopStatus; choice?: ExpChoice }[];
  /** The first stop that didn't go best; null on a perfect trip. */
  marked: number | null;
  /** The shelf item to pack to fix it; null when the backpack was fine. */
  item: ItemId | null;
  /** Whether one more of `item` fits the backpack as packed (false: make room first). */
  room: boolean;
  /** What to change, in kid words. Never names the best plan: the kid still has to find it. */
  say: string | null;
}

const bestOf = (choices: ExpChoice[]) =>
  choices.reduce((b, c) => (c.points > b.points ? c : b), choices[0]!);

/** Replays a finished trip and says where it went wrong and what would fix it. */
export function tripReview(a: Adventure, loadout: Loadout, actions: Action[]): TripReview {
  const stops: TripReview['stops'] = a.events.map(() => ({ status: 'not-reached' }));
  let marked: number | null = null;
  let before: ExpState | null = null;
  let s = start(a, loadout);
  for (const act of actions) {
    if ('ok' in s) break;
    if ('choose' in act) {
      const ev = a.events[s.event]!;
      const c = choicesOf(ev).find((x) => x.id === act.choose);
      if (c) {
        const status = c === HOME ? 'home' : c.points >= bestOf(ev.choices).points ? 'best' : 'ok';
        stops[s.event] = { status, choice: c };
        if (status !== 'best' && marked === null) {
          marked = s.event;
          before = s;
        }
      }
    }
    s = step(a, s, act);
  }
  const done = (item: ItemId | null, say: string | null): TripReview => {
    const room =
      !item ||
      ((loadout[item] ?? 0) < MAX_COPIES &&
        slotsUsed(loadout) + itemById(item)!.slots <= BACKPACK_SLOTS);
    return { stops, marked, item, room, say };
  };
  if (marked === null || !before) return done(null, null);
  const ev = a.events[marked]!;
  const best = bestOf(ev.choices);
  const why = blocked(before, best);
  if (why?.missing.length) return done(why.missing[0]!, ev.tip);
  if (why?.tired) {
    const hasFood = (Object.keys(before.uses) as ItemId[]).some(
      (id) => itemById(id)!.restores && before.uses[id],
    );
    if (hasFood) return done(null, 'Eat something first, then look at the plans again.');
    const food = a.shelf
      .filter((id) => itemById(id)!.restores)
      .sort((x, y) => itemById(y)!.restores! - itemById(x)!.restores!)[0];
    return food
      ? done(food, 'You were too tired here. Pack food for energy.')
      : done(null, 'You were too tired here. Look for a plan that is less tiring.');
  }
  return done(null, 'There was a better plan here. Look at what each one needs.');
}
